import { useRef, useState } from 'react'
import { isEqual } from 'date-fns'

import { EVENT_NAME_MOUSE_DOWN } from '../../../../../constants/event'
import {
  useEventListener,
  useNotification,
  useTimeZone,
} from '../../../../../hooks'
import { isEqualNullable } from '../../../../../utils/common'
import { formatDateToApi, toUkTimezoneDate } from '../../../../../utils/date'
import { preventDefault } from '../../../../../utils/events'

import {
  ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_EDIT_DROPDOWN_SELECTOR,
  ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_EDIT_INVALID_ERROR_KEY,
  EntityProfileHeaderInfoItemDeadlineEditState,
} from './useEntityProfileHeaderInfoItemDeadlineEdit.constants'
import {
  isDeadlineEditStateSaving,
  isDeadlineEditStateView,
} from './useEntityProfileHeaderInfoItemDeadlineEdit.utils'

export function useEntityProfileHeaderInfoItemDeadlineEdit({
  value,
  onSaveValue,
}) {
  const timezone = useTimeZone()

  const { handleAddErrorNotification } = useNotification()

  const [editState, setEditState] = useState(
    EntityProfileHeaderInfoItemDeadlineEditState.View
  )

  const [editValue, setEditValue] = useState(null)

  const [hasError, setHasError] = useState(false)

  const dateInputRef = useRef(null)
  const editContainerRef = useRef(null)

  const initialValue = value ? toUkTimezoneDate(value, timezone) : null

  const isEditing = !isDeadlineEditStateView(editState)

  const isSaving = isDeadlineEditStateSaving(editState)

  function handleStartEditing() {
    setEditValue(initialValue)
    setHasError(false)
    setEditState(EntityProfileHeaderInfoItemDeadlineEditState.Editing)
  }

  function handleChangeEditValue(nextValue) {
    setEditValue(nextValue)
    setHasError(false)
  }

  async function handleDeleteValue() {
    return onSaveValue?.(null)
  }

  async function handleSaveEditValue(event) {
    if (!isSaving) {
      if (editValue == null && dateInputRef.current?.value) {
        preventDefault(event)
        dateInputRef.current.focus()

        setHasError(true)
        handleAddErrorNotification(
          ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_EDIT_INVALID_ERROR_KEY
        )
      } else if (isEqualNullable(editValue, initialValue, isEqual)) {
        setEditState(EntityProfileHeaderInfoItemDeadlineEditState.View)
      } else {
        setEditState(EntityProfileHeaderInfoItemDeadlineEditState.Saving)

        try {
          await onSaveValue?.(formatDateToApi(editValue, timezone))

          setEditState(EntityProfileHeaderInfoItemDeadlineEditState.View)
        } catch {
          setEditState(EntityProfileHeaderInfoItemDeadlineEditState.Editing)
        }
      }
    }
  }

  function handleDocumentMouseDown(event) {
    if (isEditing) {
      const isMouseDownInsideFields = Boolean(
        editContainerRef.current?.contains(event.target)
      )

      const isMouseDownInsideDropdown = Boolean(
        event.target.closest?.(
          ENTITY_PROFILE_HEADER_INFO_ITEM_DEADLINE_EDIT_DROPDOWN_SELECTOR
        )
      )

      if (!isMouseDownInsideFields && !isMouseDownInsideDropdown) {
        handleSaveEditValue(event)
      }
    }
  }

  useEventListener({
    eventName: EVENT_NAME_MOUSE_DOWN,
    handler: handleDocumentMouseDown,
  })

  return {
    dateInputRef,
    editContainerRef,
    editValue,
    handleChangeEditValue,
    handleDeleteValue,
    handleStartEditing,
    hasError,
    isEditing,
    isSaving,
  }
}
